import React, { useState, useEffect } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";

// Context
import { useAuth } from "../../../contexts/AuthContext";

// Supabase
import { supabase } from "../../../supabase";

// Hooks
import useSnackbarNotification from "../../../hooks/useSnackbarNotification";

const AccountInformation = () => {
    const { user } = useAuth();

    const { showSuccess, showError } = useSnackbarNotification();

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        cell_number: "",
        date_of_birth: "",
        sex: "",
        training_level: "",
        employment_status: "",
        field_of_interest: "",
        country: "",
    });
    
    useEffect(() => {
        if (user?.user_metadata) {
            setFormData({
                first_name: user.user_metadata.first_name || "",
                last_name: user.user_metadata.last_name || "",
                email: user.user_metadata.email || "",
                cell_number: user.user_metadata.cell_number || "",
                date_of_birth: user.user_metadata.date_of_birth || "",
                sex: user.user_metadata.sex || "",
                training_level: user.user_metadata.training_level || "",
                employment_status: user.user_metadata.employment_status || "",
                field_of_interest: user.user_metadata.field_of_interest || "",
                country: user.user_metadata.country || "",
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({
                data: {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    cell_number: formData.cell_number,
                    date_of_birth: formData.date_of_birth,
                    sex: formData.sex,
                    training_level: formData.training_level,
                    employment_status: formData.employment_status,
                    field_of_interest: formData.field_of_interest,
                    country: formData.country,
                },
            });

            if (error) throw error;

            showSuccess("Profile updated successfully");
        } catch (error) {
            showError(
                error.message || "Failed to update profile. Please try again."
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Helper to format date to yyyy-MM-dd for date input
    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        // Pad month and day
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    return (
        <>
            <h2 className="font-bold text-xl mb-6">Account information</h2>
            <form className="mb-9" onSubmit={handleSubmit}>
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            First Name *
                        </label>
                        <input
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none"
                            placeholder="Bob"
                            required
                        />
                    </div>
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Surname *
                        </label>
                        <input
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none"
                            placeholder="Placeholder"
                            required
                        />
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Email Address
                        </label>
                        <input
                            name="email"
                            type="email"
                            value={formData.email}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none"
                            disabled
                        />
                    </div>
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Cell Number (optional)
                        </label>
                        <input
                            name="cell_number"
                            value={formData.cell_number}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none"
                        />
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Date of Birth *
                        </label>
                        <input
                            name="date_of_birth"
                            type="date"
                            value={formatDateForInput(formData.date_of_birth)}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none"
                            required
                        />
                    </div>
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Sex *
                        </label>
                        <select
                            name="sex"
                            value={formData.sex}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none appearance-none"
                            required
                        >
                            <option value="">Select</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                </div>

                {/* Country */}
                <div>
                    <label
                        htmlFor="country"
                        className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                    >
                        Country *
                    </label>
                    <input
                        name="country"
                        type="text"
                        value={countryList().getLabel(formData.country) || ""}
                        placeholder="Select your country"
                        className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none"
                        disabled
                    />
                </div>
                
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Level of training/expertise *
                        </label>
                        <select
                            name="training_level"
                            value={formData.training_level}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none appearance-none"
                            required
                        >
                            <option value="">Select</option>
                            <option value="student">Student</option>
                            <option value="junior">Junior</option>
                            <option value="senior">Senior</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Employment Status *
                        </label>
                        <select
                            name="employment_status"
                            value={formData.employment_status}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none appearance-none"
                            required
                        >
                            <option value="">Select</option>
                            <option value="full-time">Full Time</option>
                            <option value="part-time">Part Time</option>
                            <option value="unemployed">Unemployed</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                </div>

                {/* field of interest */}
                <div className="flex gap-6">
                    <div className="flex-1">
                        <label className="font-medium mb-1 block text-sm">
                            Field of interest *
                        </label>
                        <select
                            name="field_of_interest"
                            value={formData.field_of_interest}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-full mb-4 text-sm focus:outline-none appearance-none"
                            required
                        >
                            <option value="">Select</option>
                            <option value="business-consulting">
                                Business/Consulting
                            </option>
                            <option value="clinical-medicine">
                                Clinical Medicine
                            </option>
                            <option value="clinical-research">
                                Clinical Research
                            </option>
                            <option value="entrepreneurship">
                                Entrepreneurship
                            </option>
                            <option value="healthtech">
                                HealthTech
                            </option>
                            <option value="hospital-clinic-management">
                                Hospital/Clinic Management
                            </option>
                            <option value="insurance">Insurance</option>
                            <option value="marketing-sales">
                                Marketing/Sales
                            </option>
                            <option value="medical-education">
                                Medical Education
                            </option>
                            <option value="medical-communication">
                                Medical Communication
                            </option>
                            <option value="pharmaceutical">
                                Pharmaceutical
                            </option>
                            <option value="surgery">Surgery</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                </div>
                <div className="mt-3">
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`px-8 py-2 rounded-full bg-black text-white font-medium text-base mr-3 ${
                            isLoading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                </div>
            </form>
        </>
    );
};

export default AccountInformation;
